import type { Extension, Patient, Practitioner, PractitionerRole, RelatedPerson } from '@medplum/fhirtypes';
import { getAssignedRoleSlugs } from '@unify/fhir-model/resources/practitioner';
import type { Role } from '../../pages/users/types/rbac';
import { getUserRoleGroup } from '../../pages/users/types/rbac';
import type { UserPermissions } from '../../pages/users/types/permissions';
import { defaultRolePermissions } from '../../pages/users/types/permissions';
import { EXTENSION_URLS, type MedplumProfileType } from './constants';

type FhirProfile = Patient | Practitioner | RelatedPerson;

/**
 * Resolved permission state for the current user.
 * Used by PermissionContext and route guards.
 */
export interface ResolvedPermissions {
  /** Primary / default UI role (first profile role extension). */
  role: Role;
  /** All role slugs on the profile (supports multi-role users). */
  availableRoles: Role[];
  roleGroup: ReturnType<typeof getUserRoleGroup>;
  profileType: MedplumProfileType;
  permissions: Partial<UserPermissions>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getExtensionValue(extensions: Extension[] | undefined, url: string): string | undefined {
  return extensions?.find((e) => e.url === url)?.valueString;
}

function getAllExtensionCodes(extensions: Extension[] | undefined, url: string): string[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === url)
    .map((e) => e.valueCode?.trim() ?? e.valueString?.trim() ?? '')
    .filter(Boolean);
}

function inferRoleFromProfileType(profileType: MedplumProfileType): Role {
  switch (profileType) {
    case 'Patient':
      return 'client';
    case 'RelatedPerson':
      return 'client';
    case 'Practitioner':
      return 'provider';
  }
}

function parseJsonPerms(raw: string | undefined): Partial<UserPermissions> | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as Partial<UserPermissions>;
  } catch {
    return undefined;
  }
}

export interface ResolvePermissionsOptions {
  /** Session active role. Permissions follow D2 when set. */
  activeRole?: Role;
  /** Optional PractitionerRole so availableRoles can union code[]. */
  practitionerRole?: PractitionerRole;
}

/**
 * D2: defaults for activeRole, stored ui-permissions blob only when
 * activeRole === primary, then sparse ui-permission-overrides always.
 */
export function resolvePermissionsForRole(
  profile: FhirProfile,
  activeRole: Role,
  primaryRole?: Role,
): Partial<UserPermissions> {
  const primary =
    primaryRole
    ?? ((getAllExtensionCodes(profile.extension, EXTENSION_URLS.role)[0] as Role)
      || inferRoleFromProfileType(profile.resourceType as MedplumProfileType));

  let perms: Partial<UserPermissions> = { ...(defaultRolePermissions[activeRole] ?? {}) };

  if (activeRole === primary) {
    const stored = parseJsonPerms(getExtensionValue(profile.extension, EXTENSION_URLS.uiPermissions));
    if (stored) perms = { ...perms, ...stored };
  }

  const overrides = parseJsonPerms(
    getExtensionValue(profile.extension, EXTENSION_URLS.uiPermissionOverrides),
  );
  if (overrides) perms = { ...perms, ...overrides };

  return perms;
}

// ---------------------------------------------------------------------------
// Main resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the current user's role, role group, and merged UI permissions
 * from a Medplum FHIR profile resource.
 *
 * Resolution order:
 *  1. Role extension on the profile (`https://unify.health/fhir/role`)
 *  2. Fallback: infer from profile resourceType
 *  3. UI permissions: D2 for `options.activeRole` (defaults to primary)
 */
export function resolvePermissionsFromProfile(
  profile: FhirProfile,
  options?: ResolvePermissionsOptions,
): ResolvedPermissions {
  const profileType = profile.resourceType as MedplumProfileType;

  const allRoleCodes = getAllExtensionCodes(profile.extension, EXTENSION_URLS.role);
  const role: Role = (allRoleCodes[0] as Role) || inferRoleFromProfileType(profileType);
  const availableRoles: Role[] =
    profileType === 'Practitioner'
      ? (() => {
          const union = getAssignedRoleSlugs(profile as Practitioner, options?.practitionerRole) as Role[];
          return union.length > 0 ? union : [role];
        })()
      : allRoleCodes.length > 0
        ? (allRoleCodes as Role[])
        : [role];

  const roleGroup = getUserRoleGroup(role);
  const activeRole =
    options?.activeRole && availableRoles.includes(options.activeRole)
      ? options.activeRole
      : role;
  const resolvedPerms = resolvePermissionsForRole(profile, activeRole, role);

  return { role, availableRoles, roleGroup, profileType, permissions: resolvedPerms };
}
